import {Uint53} from "@cosmjs/math";
import {HttpEndpoint, TendermintClient} from "@cosmjs/tendermint-rpc";
import {assertDefined, isUint8Array} from "@cosmjs/utils";
import {AminoConverters, AminoTypes, StdFee} from "@shareledgerjs/amino";
import {EncodeObject, GeneratedType, OfflineSigner, Registry, Secp256k1HdWallet, Secp256k1Wallet} from "@shareledgerjs/signing";

import {DeliverTxResponse} from "./client";
import {calculateFee, GasPrice} from "./fee";
import {AssetExtension, createAssetActions, createAssetTypes} from "./modules/asset";
import {AuthzExtension, createAuthzTypes} from "./modules/authz";
// import {AuthExtension} from "./modules/auth";
import {BankExtension} from "./modules/bank";
import {DistributionExtension} from "./modules/distribution";
import {createDocumentActions, createDocumentTypes, DocumentExtension} from "./modules/document";
import {createElectoralActions, createElectoralTypes, ElectoralExtension} from "./modules/electoral";
import {createFeegrantActions, createFeegrantTypes, FeegrantExtension} from "./modules/feegrant";
import {createGentlemintActions, createGentlemintTypes, GentlemintExtension} from "./modules/gentlemint";
import {GovExtension} from "./modules/gov";
import {createIdActions, createIdTypes, IdExtension} from "./modules/id";
import {createNftTypes, NftExtension} from "./modules/nft";
import {createSbtTypes, SbtExtension} from "./modules/sbt";
import {SlashingExtension} from "./modules/slashing";
import {StakingExtension} from "./modules/staking";
import {createSwapActions, createSwapTypes, SwapExtension} from "./modules/swap";
import {createTokenTypes, TokenExtension} from "./modules/token";
// import {TxExtension} from "./modules/tx";
import {createWasmTypes, WasmExtension} from "./modules/wasm";
import {createDefaultAminoTypes, defaultActions, defaultRegistryTypes, SignerData, SigningClient, SigningOptions} from "./signingclient";

const registryTypes: ReadonlyArray<[string, GeneratedType]> = [
  ...defaultRegistryTypes,
  ...[
    createAssetTypes,
    createDocumentTypes,
    createElectoralTypes,
    createGentlemintTypes,
    createIdTypes,
    createSwapTypes,
    createFeegrantTypes,
    createWasmTypes,
    createTokenTypes,
    createNftTypes,
    createSbtTypes,
    createAuthzTypes
  ].reduce<ReadonlyArray<[string, GeneratedType]>>((prev, curr) => [...prev, ...curr()], [])
];

function createRegistry(): Registry {
  return new Registry(registryTypes);
}

function createAminoTypes(): AminoConverters {
  return [
    createDefaultAminoTypes
    // not support amino
  ].reduce((prev, curr) => ({...prev, ...curr()}), {});
}

const actions: Record<string, string> = {
  ...defaultActions,
  ...[
    createAssetActions,
    createDocumentActions,
    createElectoralActions,
    createGentlemintActions,
    createIdActions,
    createSwapActions,
    createFeegrantActions
  ].reduce((prev, curr) => ({...prev, ...curr()}), {})
};

export interface ShareledgerSigningClient
  // AuthExtension,
  extends BankExtension,
    DistributionExtension,
    GovExtension,
    FeegrantExtension,
    SlashingExtension,
    StakingExtension,
    // TxExtension,
    AssetExtension,
    DocumentExtension,
    ElectoralExtension,
    GentlemintExtension,
    IdExtension,
    SwapExtension,
    WasmExtension,
    TokenExtension,
    NftExtension,
    SbtExtension,
    AuthzExtension {}

// @AuthExtension
@BankExtension
@DistributionExtension
@GovExtension
@FeegrantExtension
@SlashingExtension
@StakingExtension
// @TxExtension
@AssetExtension
@DocumentExtension
@ElectoralExtension
@GentlemintExtension
@IdExtension
@SwapExtension
@WasmExtension
@TokenExtension
@NftExtension
@SbtExtension
@AuthzExtension
export class ShareledgerSigningClient extends SigningClient {
  public constructor(tmClient: TendermintClient | undefined, signer?: OfflineSigner, options: SigningOptions = {}) {
    super(tmClient, signer, {
      ...options,
      aminoTypes: new AminoTypes(createAminoTypes()),
      registry: createRegistry()
    });
  }

  /**
   * Creates an instance by connecting to the given Tendermint RPC endpoint.
   *
   * This uses auto-detection to decide between a Tendermint 0.37 and 0.34 client.
   * To set the Tendermint client explicitly, use `create`.
   */
  public static async connect(endpoint: string | HttpEndpoint, options?: SigningOptions): Promise<ShareledgerSigningClient> {
    const tmClient = await this.createTmClient(endpoint);
    return ShareledgerSigningClient.create(tmClient, options);
  }

  /**
   * Creates an instance from a manually created Tendermint client.
   * Use this to use `Tendermint37Client` instead of `Tendermint34Client`.
   */
  public static async create(tmClient: TendermintClient, options?: SigningOptions): Promise<ShareledgerSigningClient> {
    return new ShareledgerSigningClient(tmClient, undefined, options);
  }

  /**
   * Connect with blockchain RPC using mnemonic
   * @param endpoint RPC endpoint
   * @param mnemonic Mnemonic
   * @param options Signing options
   *
   * For now this uses the Tendermint 0.34 client. If you need Tendermint 0.37
   * support, see `createWithSigner`.
   */
  public static async connectWithSigner(
    endpoint: string | HttpEndpoint,
    mnemonic: string,
    options?: SigningOptions
  ): Promise<ShareledgerSigningClient>;
  /**
   * Connect with blockchain RPC using private key
   * @param endpoint RPC endpoint
   * @param privKey Private key
   * @param options Signing options
   *
   * For now this uses the Tendermint 0.34 client. If you need Tendermint 0.37
   * support, see `createWithSigner`.
   */
  public static async connectWithSigner(
    endpoint: string | HttpEndpoint,
    privKey: string | Uint8Array,
    options?: SigningOptions
  ): Promise<ShareledgerSigningClient>;
  /**
   * Connect with blockchain RPC using offline signer
   * @param endpoint RPC endpoint
   * @param signer Signer
   * @param options Signing options
   *
   * For now this uses the Tendermint 0.34 client. If you need Tendermint 0.37
   * support, see `createWithSigner`.
   */
  public static async connectWithSigner(
    endpoint: string | HttpEndpoint,
    signer: OfflineSigner,
    options?: SigningOptions
  ): Promise<ShareledgerSigningClient>;

  public static async connectWithSigner(
    endpoint: string | HttpEndpoint,
    signer: string | Uint8Array | OfflineSigner,
    options: SigningOptions = {}
  ): Promise<ShareledgerSigningClient> {
    const tmClient = await this.createTmClient(endpoint);
    signer = typeof signer === "string" || isUint8Array(signer) || Buffer.isBuffer(signer) ? await this.createSigner(signer) : signer;
    return ShareledgerSigningClient.createWithSigner(tmClient, signer, options);
  }

  /**
   * Connect with blockchain RPC using mnemonic
   * @param endpoint RPC endpoint
   * @param mnemonic Mnemonic
   * @param options Signing options
   *
   * Creates an instance from a manually created Tendermint client.
   * Use this to use `Tendermint37Client` instead of `Tendermint34Client`.
   */
  public static async createWithSigner(
    tmClient: TendermintClient,
    mnemonic: string,
    options?: SigningOptions
  ): Promise<ShareledgerSigningClient>;
  /**
   * Connect with blockchain RPC using private key
   * @param endpoint RPC endpoint
   * @param privKey Private key
   * @param options Signing options
   *
   * Creates an instance from a manually created Tendermint client.
   * Use this to use `Tendermint37Client` instead of `Tendermint34Client`.
   */
  public static async createWithSigner(
    tmClient: TendermintClient,
    privKey: string | Uint8Array,
    options?: SigningOptions
  ): Promise<ShareledgerSigningClient>;
  /**
   * Connect with blockchain RPC using offline signer
   * @param endpoint RPC endpoint
   * @param signer Signer
   * @param options Signing options
   *
   * Creates an instance from a manually created Tendermint client.
   * Use this to use `Tendermint37Client` instead of `Tendermint34Client`.
   */
  public static async createWithSigner(
    tmClient: TendermintClient,
    signer: OfflineSigner,
    options?: SigningOptions
  ): Promise<ShareledgerSigningClient>;

  public static async createWithSigner(
    tmClient: TendermintClient,
    signer: string | Uint8Array | OfflineSigner,
    options: SigningOptions = {}
  ): Promise<ShareledgerSigningClient> {
    signer = typeof signer === "string" || isUint8Array(signer) || Buffer.isBuffer(signer) ? await this.createSigner(signer) : signer;
    return new ShareledgerSigningClient(tmClient, signer, options);
  }

  public static async offline(mnemonic: string, options?: SigningOptions): Promise<ShareledgerSigningClient>;
  public static async offline(privKey: string | Uint8Array, options?: SigningOptions): Promise<ShareledgerSigningClient>;
  public static async offline(signer: OfflineSigner, options?: SigningOptions): Promise<ShareledgerSigningClient>;
  public static async offline(
    signer: string | Uint8Array | OfflineSigner,
    options: SigningOptions = {}
  ): Promise<ShareledgerSigningClient> {
    signer = typeof signer === "string" || isUint8Array(signer) || Buffer.isBuffer(signer) ? await this.createSigner(signer) : signer;
    return new ShareledgerSigningClient(undefined, signer, options);
  }

  protected static async createSigner(input: string | Uint8Array) {
    let signer: OfflineSigner;
    if (typeof input === "string" && !/^[A-F0-9]+$/i.test(input)) {
      signer = await Secp256k1HdWallet.fromMnemonic(input);
    } else {
      signer = await Secp256k1Wallet.fromKey(isUint8Array(input) || Buffer.isBuffer(input) ? input : Buffer.from(input, "hex"));
    }
    return signer;
  }

  public override async withSigner(signer: string | Uint8Array | OfflineSigner): Promise<ShareledgerSigningClient> {
    signer =
      typeof signer === "string" || isUint8Array(signer) || Buffer.isBuffer(signer)
        ? await ShareledgerSigningClient.createSigner(signer)
        : signer;
    await super.withSigner(signer);
    return this;
  }

  public override withoutSigner(): ShareledgerSigningClient {
    super.withoutSigner();
    return this;
  }

  public override async signAndBroadcast(
    signerAddress: string,
    messages: readonly EncodeObject[] | Uint8Array,
    fee?: (StdFee & {gasPrice?: GasPrice}) | number,
    memo?: string,
    explicitSignerData?: SignerData
  ): Promise<DeliverTxResponse> {
    if (!fee || (typeof fee !== "number" && !fee.amount && !fee.gas)) {
      const msgs = Buffer.isBuffer(messages) || isUint8Array(messages) ? this.registry.decodeTxBody(messages).messages : messages;
      fee = await this.estimate(signerAddress, msgs, memo, fee, explicitSignerData);
    }
    return super.signAndBroadcast(signerAddress, messages, fee, memo, explicitSignerData);
  }

  public override async signAndBroadcastSync(
    signerAddress: string,
    messages: readonly EncodeObject[] | Uint8Array,
    fee?: (StdFee & {gasPrice?: GasPrice}) | number,
    memo?: string,
    explicitSignerData?: SignerData
  ): Promise<string> {
    if (!fee || (typeof fee !== "number" && !fee.amount && !fee.gas)) {
      const msgs = Buffer.isBuffer(messages) || isUint8Array(messages) ? this.registry.decodeTxBody(messages).messages : messages;
      fee = await this.estimate(signerAddress, msgs, memo, fee, explicitSignerData);
    }
    return super.signAndBroadcastSync(signerAddress, messages, fee, memo, explicitSignerData);
  }

  public override async sign(
    signerAddress: string,
    messages: readonly EncodeObject[] | Uint8Array,
    fee?: (StdFee & {gasPrice?: GasPrice}) | number,
    memo?: string,
    explicitSignerData?: SignerData
  ): Promise<Uint8Array> {
    if (!fee || (typeof fee !== "number" && !fee.amount && !fee.gas)) {
      const msgs = Buffer.isBuffer(messages) || isUint8Array(messages) ? this.registry.decodeTxBody(messages).messages : messages;
      fee = await this.estimate(signerAddress, msgs, memo, fee, explicitSignerData);
    }
    return super.sign(signerAddress, messages, fee, memo, explicitSignerData);
  }

  public async estimate(
    signerAddress: string,
    messages: readonly EncodeObject[],
    memo?: string,
    fee?: (StdFee & {gasPrice?: GasPrice}) | number,
    explicitSignerData?: SignerData
  ) {
    const feeEstimation = await this.gentlemint.estimateFee(
      signerAddress,
      messages.map((msg) => actions[msg.typeUrl])
    );
    const multiplier = typeof fee === "number" ? fee : 1.275;
    fee = typeof fee === "number" ? {} : fee;
    const gasEstimation = await this.simulate(signerAddress, messages, memo, fee, explicitSignerData);
    const buff = Math.round(gasEstimation * multiplier);
    fee = {...fee, gas: new Uint53(buff).toString()};
    if (feeEstimation.fee) {
      // has fixed fee defined by gentlemint module
      return {
        ...fee,
        amount: [feeEstimation.fee]
      };
    }
    // if no fixed fee defined, fallback to gasPrice check
    // TODO when globalfee is avail, get min gas price from that too
    const gasPrice = fee.gasPrice || this.gasPrice;
    assertDefined(gasPrice, "Gas price must be set in the client options when fee amount and gas is not specified.");
    return {
      ...fee,
      ...calculateFee(buff, gasPrice)
    };
  }
}
